import React, { FC } from 'react'
import { Redirect } from 'react-router-dom'
import LogoTransfix from 'transfix-ui/components/LogoTransfix/LogoTransfix'
import { Grid, Link, makeStyles } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useGetDriverValidationShipment } from 'apps/fleet-planner/types'
import { LockedIcon, OpenInNewTabIcon, SmartphoneMessagingIcon } from 'transfix-icons'
import { Spinner } from 'transfix-ui'
import { DriverValidationErrorDisplay } from './DriverValidationErrorDisplay'
import { QrDisplay } from './QrDisplay'
import { StopsSummary } from './stops-summary/StopsSummary'

export const useTextStyles = makeStyles({
  bodyTwo: {
    padding: 0,
  },
  bodyLinkTwo: {
    fontWeight: 600,
    padding: 0,
  },
  caption: {
    color: brandColors.coolGray5,
  },
  subtitleOne: {
    fontSize: 18,
  },
  subtitleTwo: {
    color: brandColors.coolGray5,
  },
})

export const DriverValidationPage: FC = () => {
  const { data, error, loading } = useGetDriverValidationShipment({})
  const { share, shipment, statusShare } = data || {}
  const { dispatcher, externalId, id: shipmentId, motorCarrier, stops = [] } = shipment || {}
  const { id: motorCarrierId, name: carrierName } = motorCarrier || {}
  const { sharedWithDriver } = share || {}
  const { id: driverId, name: driverName } = sharedWithDriver || {}
  const { fullName: dispatcherName } = dispatcher || {}
  const { url: checkinUrl } = statusShare || {}

  const classes = useTextStyles()

  const { trackEvent } = useFMSAnalytics()

  if (loading) {
    return <Spinner />
  }

  if (error) {
    if (error.status === 401) {
      trackEvent('Fraud', 'DRIVER_VALIDATION_UNAUTHORIZED')
      return <Redirect to='/securebooking-expired' />
    }
    trackEvent('Fraud', 'DRIVER_VALIDATION_ERROR')
    return <DriverValidationErrorDisplay />
  }

  trackEvent('Fraud', 'DRIVER_VALIDATION_SUCCESS', {
    shipmentId,
    motorCarrierId,
    driverId,
  })

  return (
    <Box display='flex' justifyContent='center'>
      <Box paddingX='16px'>
        <Box marginY='32px'>
          <Box display='flex' flexDirection='column' alignItems='center' marginBottom='5px'>
            <Box marginBottom='7px'>
              <LogoTransfix width={130} />
            </Box>
            <QrDisplay
              shipmentId={shipmentId}
              motorCarrierId={motorCarrierId}
              driverId={driverId}
            />
          </Box>
          <Box display='flex' justifyContent='start' alignItems='start' marginBottom='20px'>
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              bgcolor={brandColors.coolGray1}
              marginRight='12px'
              width={42}
              height={42}
              borderRadius='12px'>
              <LockedIcon color='coolGray5' size='medium' />
            </Box>
            <Box display='flex' flexDirection='column'>
              <Typography variant='body2' className={classes.bodyLinkTwo}>
                Activate secure code at check-in
              </Typography>
              <Typography variant='caption' className={classes.caption}>
                Turn the brightness up on your phone
              </Typography>
            </Box>
          </Box>
          <Box display='flex' justifyContent='start' alignItems='start'>
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              bgcolor={brandColors.coolGray1}
              marginRight='12px'
              width={42}
              height={42}
              borderRadius='12px'>
              <SmartphoneMessagingIcon color='coolGray5' size='medium' />
            </Box>
            <Box display='flex' flexDirection='column'>
              <Typography variant='body2' className={classes.bodyLinkTwo}>
                Show screen to facility manager
              </Typography>
              <Typography variant='caption' className={classes.caption}>
                They will review and scan your code
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          marginBottom='16px'
          boxShadow='0px 1px 2px rgba(0, 0, 0, 0.15)'
          border={1}
          borderColor={brandColors.coolGray2}
          borderRadius='4px'>
          <Box bgcolor={brandColors.coolGray1} padding='8px 20px'>
            <Typography variant='overline'>Load #{externalId}</Typography>
          </Box>
          <Box padding='16px'>
            <StopsSummary stops={stops} />
          </Box>
          <Box padding='8px 16px' borderTop={1} borderColor={brandColors.coolGray2}>
            <Box paddingY='8px'>
              <Grid container>
                <Grid item md={4} xs={4}>
                  <Typography variant='subtitle2' className={classes.subtitleTwo}>
                    Carrier
                  </Typography>
                </Grid>
                <Grid item md={8} xs={8}>
                  <Typography variant='body2' className={classes.bodyTwo} align='right'>
                    {carrierName}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Box paddingY='8px'>
              <Grid container>
                <Grid item md={4} xs={4}>
                  <Typography variant='subtitle2' className={classes.subtitleTwo}>
                    Driver
                  </Typography>
                </Grid>
                <Grid item md={8} xs={8}>
                  <Typography variant='body2' className={classes.bodyTwo} align='right'>
                    {driverName}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Box paddingY='8px'>
              <Grid container>
                <Grid item md={4} xs={4}>
                  <Typography variant='subtitle2' className={classes.subtitleTwo}>
                    Dispatcher
                  </Typography>
                </Grid>
                <Grid item md={8} xs={8}>
                  <Typography variant='body2' className={classes.bodyTwo} align='right'>
                    {dispatcherName}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Box padding='16px' borderTop={1} borderColor={brandColors.coolGray2}>
            <Link href={checkinUrl} target='_blank'>
              <Box display='flex' alignItems='center'>
                <OpenInNewTabIcon color='skyBlue6' size='large' />
                <Box marginLeft='8px'>
                  <Typography variant='body2' className={`${classes.bodyLinkTwo}`}>
                    Check in on the Transfix App
                  </Typography>
                </Box>
              </Box>
            </Link>
          </Box>
        </Box>
        <Box
          borderRadius='4px'
          bgcolor={brandColors.coolGray1}
          padding='12px 16px'
          marginBottom='30px'>
          <Typography variant='caption'>
            If something isn’t working correctly or doesn’t look correct{' '}
            <Link href='tel:6469484480'>
              <b>Call Transfix</b>
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
